import { lazy, type FC, type LazyExoticComponent, type ReactElement } from 'react';
import { IndustryRoutes, ProjectRoutes } from './routes.tsx';
import type { KeyTakeaway } from './data-blog';
import { Image } from './components/image';

export type KpiData = { label: string, value: string, unit?: never } | { label: string, value: number | { from: number, to: number }, unit?: string };

export interface CaseStudyProps {
  cta?: ReactElement | null;
}

export interface ProjectData {
  title: string;
  menuLabel?: { company: string; label: string; show?: true };
  subtitle?: string;
  tags: string[];
  showcase: ReactElement,
  banner: ReactElement,
  kpis?: KpiData[];
  info?: {
    location: string;
    employees: number;
    revenues: string;
  },
  summary: KeyTakeaway[];
  keyTakeaways: KeyTakeaway[];
  content: LazyExoticComponent<FC<ProjectData & CaseStudyProps>> | undefined;
  pdfUri?: string;
  description?: string;
  industry: IndustryRoutes;
}

export const PROJECT_DATA: Record<ProjectRoutes, ProjectData> = {
  [ProjectRoutes.INDUSTRIAL_TECHNICIANS]: {
    industry: IndustryRoutes['MANUFACTURING'],
    title: 'Field troubleshooting with AI',
    menuLabel: { company: 'Industrial Group', label: 'Field Service AI copilot', show: true },
    subtitle: 'Field root cause resolution in minutes with a multi-lingual AI solution',
    tags: ['AI Field Assistance', 'Knowledge Management', 'AI Agents'],
    showcase: <Image src='/projects/chernobyl_coffin.jpg' alt='Field troubleshooting with AI' />,
    banner: <Image src='/projects/chernobyl_coffin.jpg' alt='Work In Progress' />,
    info: {
      location: 'European Union',
      employees: 250,
      revenues: '€100M',
    },
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { unit: '%', value: 80, label: 'MTTD' },
      { unit: '%', value: 15, label: 'MTTR' },
      { unit: '%', value: 25, label: 'FTFR' },
      { unit: '%', value: 40, label: 'reduction in Technician Onboarding Time' },
    ]*/,
    summary: [
      {
        title: 'Pain point',
        content: 'Fragmented and overwhelming documentation, uneven language coverage, weak knowledge transfer between teams',
      },
      {
        title: 'Solution',
        content: 'AI with instant documentation access, identify missing documentation, and capture of incidents symptoms and resolution steps, making it searchable company-wide',
      },
      {
        title: 'Outcome',
        content: 'Reduced operational costs, shorter mean time to resolution and reduced risk of expert dependence',
      }
    ],
    keyTakeaways: [
      {
        title: 'Field Environments Demand Mobile-First Design Beyond Responsive Interfaces',
        content: 'Field technicians work in challenging conditions: poor lighting, wearing gloves, operating heavy equipment, or working at height. Voice-enabled search and large, touch-optimized interfaces become essential rather than convenient. Systems designed for office environments fail in actual field deployment regardless of technical sophistication.'
      },
      {
        title: 'Operational Knowledge Capture Requires Structured Workflow Integration',
        content: 'Capturing tribal knowledge cannot depend on technicians voluntarily documenting solutions after completing interventions. Successful knowledge capture integrates directly into mandatory reporting workflows, automatically structuring field notes and intervention reports into searchable knowledge entries after expert review. This transforms routine documentation into lasting organizational intelligence.'
      },
      {
        title: 'Cross-Lingual Operations Reveal Hidden Documentation Gaps',
        content: 'Deploying knowledge systems across multiple languages exposes inconsistencies and gaps that remain invisible in single-language operations. Equipment covered extensively in documentation for one market may lack critical maintenance procedures in another. These gaps only become apparent when technicians query the system in different languages, revealing documentation priorities that need addressing.'
      }
    ],
    content: lazy(() => import('./case-studies/maintenance-field-service.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.IndustrialTechnicians {...PROJECT_DATA[ProjectRoutes.INDUSTRIAL_TECHNICIANS]} cta={cta} /> }))),
    description: 'Providing instant troubleshooting and operational expertise for industrial technicians, learning tribal knowledge and reducing diagnostic time.',
    // pdfUri: '/projects/capmo-ai.pdf',
  },
  [ProjectRoutes.CAPMO_AI]: {
    industry: IndustryRoutes['CONSTRUCTION'],
    title: 'Accelerating Construction Projects with Capmo AI',
    menuLabel: { company: 'Capmo AI', label: 'Construction Management', show: true },
    subtitle: 'AI powered data retrieval and reporting tasks for construction project management',
    tags: ['Knowledge Management', 'AI Agents', 'Construction'],
    showcase: <Image src='/projects/construction_photo.jpg' alt='Accelerating Construction Projects with Capmo AI' />,
    banner: <Image src='/projects/construction.png' alt='Workers in a construction site' />,
    info: {
      location: 'Germany',
      employees: 150,
      revenues: '€30M',
    },
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { unit: 'x', value: 4, label: 'faster information retrieval' },
      { unit: '%', value: 38, label: 'reduction in manual workloads' },
      { unit: '%', value: 50, label: 'faster defect management' },
      { unit: 's', value: { from: 10, to: 2 }, label: 'average assistant response time' },
    ]*/,
    summary: [
      {
        title: 'Pain point',
        content: 'Customers struggle to find their data quickly despite strong investment in user interface, due to volume and complexity of documents',
      },
      {
        title: 'Solution',
        content: 'Integrate AI with instant data access in natural language for every employee',
      },
      {
        title: 'Outcome',
        content: 'Increased revenues and improved Net Promoter Score (NPS)',
      }
    ],
    keyTakeaways: [
      {
        title: 'Enterprise AI Implementation Requires Robust Data Governance',
        content: 'Successful AI deployment depends on establishing proper data access controls, audit trails, and compliance frameworks from the project start. These governance requirements significantly impact system architecture decisions.'
      },
      {
        title: 'User Adoption Success Depends on Workflow Integration',
        content: 'AI tools achieve highest adoption rates when integrated seamlessly into existing business processes. Users prefer enhanced versions of familiar workflows over entirely new interfaces or procedures.'
      },
      {
        title: 'Search Performance Optimization Requires Early Planning',
        content: 'Vector database performance becomes critical at enterprise scale. Efficient document treatment, efficient vector database, parallel processing and event replayability are mandatory requirements for large scale RAG.'
      }
    ],
    content: lazy(() => import('./case-studies/capmo-ai.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.CapmoAI {...PROJECT_DATA[ProjectRoutes.CAPMO_AI]} cta={cta} /> }))),
    description: 'Intelligent data management for construction projects using AI to unify site information, optimizing search and incident management times.',
    // pdfUri: '/projects/capmo-ai.pdf',
  },
  // [ProjectRoutes.AUDIT_FIRM]: {
  //   title: 'Streamline customer documentation analysis with an on-premise multi-lingual AI knowledge management solution',
  //   menuLabel: { company: 'Audit Firm', label: 'Technician AI copilot', show: true },
  //   subtitle: 'Root cause resolution in minutes with a multi-lingual AI knowledge management solution',
  //   tags: ['Compliance', 'AI Knowledge Management', 'Workflow Optimization'],
  //   showcase: <Image src='/projects/technician_agent.jpg' alt='Streamline customer documentation analysis with an on-premise multi-lingual AI knowledge management solution' />,
  //   banner: <Image src='/projects/construction.png' alt='Work In Progress' />,
  // eslint-disable-next-line no-constant-binary-expression
  //   kpis: [] ?? [
  //     { unit: '%', value: 80, label: 'faster root cause identification time' },
  //     { unit: '%', value: 15, label: 'faster incident resolution' },
  //     { unit: '%', value: 25, label: 'more jobs resolved on first visit' },
  //     { unit: '%', value: 40, label: 'faster new hire on-boarding time' },
  //   ],
  //   content: undefined,
  //   // content: lazy(() => import('./case-studies/capmo-ai.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.CapmoAI {...PROJECT_DATA[ProjectRoutes.CAPMO_AI]} cta={cta} /> }))),
  //   description: 'Intelligent data management for construction projects using AI to unify site information, optimizing search and incident management times.',
  //   // pdfUri: '/projects/capmo-ai.pdf',
  // },
  [ProjectRoutes.MOBILISIGHTS]: {
    industry: IndustryRoutes['AUTOMOTIVE'],
    title: 'Powering Global Automotive Intelligence with Mobilisights',
    menuLabel: { company: 'Stellantis', label: 'Automotive Intelligence', show: true },
    subtitle: 'Unifying Stellantis\' global automotive brands for real-time connected car telemetry analytics',
    tags: ['Predictive Analytics', 'Data Platform', 'Automotive'],
    showcase: <Image src='/projects/mobilisights.png' alt='Fleet management at scale with Stellantis' />,
    banner: <Image src='/projects/car.png' alt='A car on a road' />,
    info: {
      location: 'Global',
      employees: 300_000,
      revenues: '€37B',
    },
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { value: 14, label: 'global automotive brands unified' },
      { unit: 'M', value: 15, label: 'connected vehicles across NA and EU' },
      { unit: 'TB', value: 30, label: 'data points processed per month' },
      { value: '€2B', label: 'projected annual revenues' },
    ]*/,
    summary: [
      {
        title: 'Pain point',
        content: '14 disparate automotive brands with 14 unique APIs, systems and teams was hurting customer acquisition and operational costs',
      },
      {
        title: 'Solution',
        content: 'A single and redesigned system and API, with a zero-downtime migration strategy to preserve business continuity',
      },
      {
        title: 'Outcome',
        content: '15M connected vehicles delivering actionable insights across two continents, enabling smart insurances, fleet management and mobility solutions',
      }
    ],
    keyTakeaways: [
      {
        title: 'Vehicle Data Collection Patterns Require Burst-Resilient Pipeline Architecture',
        content: 'Connected vehicles accumulate telemetry data during periods without network coverage (underground parking, remote areas), then transmit large data volumes when connectivity resumes. These commonly overlooked transmission patterns can overwhelm standard streaming architectures. Designing specialized buffering and rate-limiting systems becomes essential for maintaining consistent data processing performance.'
      },
      {
        title: 'Enterprise Data Integration Demands Client-Specific Reliability Controls',
        content: 'Enterprise clients require customized data delivery patterns based on their existing workflows and system capabilities. Data consistency becomes critical as integration errors or unexpected volume spikes can impact client operations. Implementing robust back pressure management and deduplication controls is essential for maintaining stable client relationships.'
      },
      {
        title: 'Zero-Downtime Migration Success Depends on Parallel System Architecture',
        content: 'Enterprise platforms handling continuous data streams require parallel infrastructure during migration phases. Traditional blue-green deployment strategies must be adapted for real-time data systems where service interruption impacts millions of connected devices simultaneously.'
      }
    ],
    content: lazy(() => import('./case-studies/mobilisights.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.Mobilisights {...PROJECT_DATA[ProjectRoutes.MOBILISIGHTS]} cta={cta} /> }))),
    description: `Modern data platform, processing 30TB monthly from 15M connected vehicles across Stellantis brands, enabling real-time connected car telemetry analytics.`,
    // pdfUri: '/projects/mobilisights.pdf',
  },
  // [ProjectRoutes.PUBLISHING_PLATFORM]: {
  //   industry: IndustryRoutes['EDUCATION'],
  //   title: 'Accelerating book publishing: idea to shelves in days',
  //   menuLabel: { company: 'Publishing House', label: 'Publishing & AI Book Generation', show: true },
  //   subtitle: 'Using Generative AI to turn ideas and content into books published in store within days',
  //   tags: ['Media & Entertainment', 'AI', 'Book Creation'],
  //   showcase: <Image src='/projects/story_editor.jpg' alt='Accelerating book publishing: idea to shelves in days' />,
  //   banner: <Image src='/projects/publishing-store.jpg' alt='A commercial printing machine' />,
  //   // eslint-disable-next-line no-constant-binary-expression
  //   kpis: []/* ?? [
  //     { value: '70k', label: 'pages generated per hour' },
  //     { unit: 'h', value: 24, label: 'from idea to print' },
  //     { unit: '%', value: 50, label: 'faster defect management' },
  //     // { unit: 's', value: { from: 10, to: 2 }, label: 'average assistant response time' },
  //   ]*/,
  //   content: lazy(() => import('./case-studies/publishing-platform.tsx').then(module => ({ default: () => <module.BookCreator {...PROJECT_DATA[ProjectRoutes.PUBLISHING_PLATFORM]} /> }))),
  //   description: 'AI-powered publishing platform transforming podcasts, videos, and notes into complete books in minutes.',
  //   // pdfUri: '/projects/publishing-platform.pdf',
  // },
  [ProjectRoutes.VEGETOIL]: {
    industry: IndustryRoutes['ENERGY'],
    title: 'Operations Digitalisation with Vegetoil',
    menuLabel: { company: 'Vegetoil', label: 'Operations Digitalisation', show: true },
    subtitle: 'Blending sustainability with operational efficiency in biofuel production',
    tags: ['Operations Platform', 'Intelligent Automation', 'Data Entry & Tracking'],
    showcase: <Image src='/projects/logistics.png' alt='Digitalising operations with Vegetoil' />,
    banner: <Image src='/projects/vegetoil_banner.png' alt='A pumping station with multiple fuel options' style={{ objectPosition: 'center 100%' }} />,
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { unit: '%', value: 66, label: 'faster barrel inventory and tracking' },
      { unit: '%', value: 90, label: 'reduction in data entry and reporting' },
      { unit: '%', value: 72, label: 'decrease in human errors and accuracy' },
      { unit: 's', value: 10, label: 'average time to locate a barrel' },
    ]*/,
    summary: [
      {
        title: 'Pain point',
        content: 'Loss of assets and missed deadlines caused by manual paper process and poor employee digital skills',
      },
      {
        title: 'Solution',
        content: 'NFC based asset tracking, digitalised delivery and treatment processes with a mobile app, and supervisor control panel',
      },
      {
        title: 'Outcome',
        content: 'Reduced operational costs and reduced risk of asset loss',
      }
    ],
    keyTakeaways: [
      {
        title: 'Location Verification Technology Prevents Costly Collection Errors',
        content: 'QR codes at supply points enable offline location verification in areas with poor connectivity, while NFC tags on barrels provide real-time tracking. This dual approach prevents wrong-location pickups and ensures accurate inventory flow, critical issues that can cost logistics companies 15-20% in operational efficiency.'
      },
      {
        title: 'Real-Time Visibility Transforms Risk Management in Commodity Processing',
        content: 'Instant access to barrel status and location data enables proactive quality control measures that prevent contamination incidents. Companies processing regulated materials like biofuels gain significant compliance advantages through automated audit trail generation.'
      },
      {
        title: 'Mobile-First ERP Integration Drives Higher ROI Than Desktop-Centric Systems',
        content: 'Field workers spend 80% of their time away from desks, making mobile accessibility critical for data accuracy. Systems designed for mobile-first usage achieve 3x higher data entry completion rates and significantly better operational visibility than traditional ERP implementations.'
      }
    ],
    content: lazy(() => import('./case-studies/vegetoil.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.Vegetoil {...PROJECT_DATA[ProjectRoutes.VEGETOIL]} cta={cta} /> }))),
    description: `Digital transformation for biofuel company using mobile apps with QR/NFC technology and custom ERP to reduce reporting time and inventory errors.`,
    // pdfUri: '/projects/vegetoil.pdf',
  },
  [ProjectRoutes.STUDENT_ORIENTATION]: {
    industry: IndustryRoutes['EDUCATION'],
    title: 'Orienting students in their journey',
    menuLabel: { company: 'FH Upper Austria', label: 'University of Applied Sciences' },
    subtitle: 'Providing a multi-lingual AI voice agent to orient student across the facility, courses and capabilities of the establishment.',
    tags: ['AI Agents', 'Knowledge Management', 'Education'],
    showcase: <Image src='/projects/students.jpg' alt='Orienting students in their journey' />,
    banner: <Image src='/projects/research.png' alt='Futuristic globe above research data in clouds' style={{ objectPosition: 'center -80px' }} />,
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { value: '40+', label: 'languages supported' },
      { unit: 's', value: 2, label: 'response time' },
      { unit: '%', value: 20, label: 'less direct queries' },
      // { unit: '+ TB', value: 3, label: 'scientific literature managed daily' },
      // { unit: '%', value: 99.95, label: 'platform uptime' },
    ]*/,
    summary: [],
    keyTakeaways: [],
    content: undefined,
    // content: lazy(() => import('./case-studies/research-automation-platform.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.ResearchAutomationPlatform {...PROJECT_DATA[ProjectRoutes.RESEARCH_AUTOMATION_PLATFORM]} cta={cta} /> }))),
    description: 'AI platform automating literature reviews with semantic search across millions of papers to accelerate scientific discovery and reduce research time.',
    // pdfUri: '/projects/research-automation-platform.pdf',
  },
  [ProjectRoutes.UNIVERSUM_PRESALES]: {
    industry: IndustryRoutes['EVENTS'],
    title: 'Pre-Sales Qualification with AI',
    menuLabel: { company: 'Universum', label: 'Pre-Sales AI Agent' },
    subtitle: 'Auto-enriching and qualifying inbound leads so sales teams pursue only their best opportunities',
    tags: ['AI Agents', 'Sales Enablement', 'Events'],
    showcase: <Image src='/projects/sales-meeting.jpg' alt='Pre-sales AI agent qualifying leads' />,
    banner: <Image src='/projects/sales-meeting.jpg' alt='A sales team in a meeting' />,
    kpis: [],
    summary: [],
    keyTakeaways: [],
    content: undefined,
    description: 'AI pre-sales agent that auto-enriches and qualifies inbound leads at scale, leaving sales teams a vetted shortlist to act on.',
  },
  [ProjectRoutes.ASSET_MANAGEMENT_PLATFORM]: {
    industry: IndustryRoutes['FINANCE'],
    title: 'Building an Asset Management Platform',
    menuLabel: { company: 'Investment Firm', label: 'Asset Management Platform' },
    subtitle: 'Allocating client capital across funds with a proprietary algorithm for the best performance',
    tags: ['Data Platform', 'Predictive Analytics', 'Finance'],
    showcase: <Image src='/projects/investment-advisor.jpg' alt='Asset management platform' />,
    banner: <Image src='/projects/investment-advisor.jpg' alt='Algorithmic asset allocation' />,
    kpis: [],
    summary: [],
    keyTakeaways: [],
    content: undefined,
    description: 'End-to-end asset management platform allocating client capital across funds with a proprietary fund-selection algorithm.',
  },
  [ProjectRoutes.COMMUNICATION_PLATFORM]: {
    industry: IndustryRoutes['HEALTHCARE'],
    title: `Transforming Outreach with a Communication Platform`,
    subtitle: 'Empowering insurance sales teams and accelerating onboarding with advanced communication solutions',
    menuLabel: { company: 'Assorea', label: 'Communication Platform' },
    tags: ['Communication Platform', 'Sales Enablement', 'Insurtech'],
    showcase: <Image src='/projects/insurance.png' alt={`Transforming outreach with a communication platform`} />,
    banner: <Image src='/projects/office.jpg' alt='A team of people in an office' style={{ objectPosition: 'center 20%' }} />,
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { unit: '%', value: 10, label: 'increase in sales performance' },
      { unit: 'x', value: 2, label: 'faster training and onboarding time' },
      { unit: '%', value: 25, label: 'boost employee interaction efficiency' },
      { unit: '%', value: 99.99, label: 'platform and systems uptime' },
    ]*/,
    summary: [],
    keyTakeaways: [
      {
        title: 'Video-First Sales Strategy Requires Systematic Training Integration',
        content: 'Successful video communication adoption depends on embedding recording and review capabilities directly into training workflows. Organizations that treat video as an add-on to existing processes see limited adoption compared to those that redesign training around video-first methodologies.'
      },
      {
        title: 'AI-Powered Supervision Scales Management Effectiveness',
        content: 'Intelligent monitoring systems enable managers to focus intervention efforts where they create maximum impact. Real-time alerts for compliance risks and missed opportunities prevent issues before they escalate, while silent joining capabilities allow for contextual coaching without disrupting client relationships.'
      },
      {
        title: 'Regulatory Compliance Automation Reduces Risk While Improving Performance',
        content: 'Automated transcription and AI-powered compliance monitoring transform regulatory adherence from a manual overhead cost into a competitive advantage. Organizations that embed compliance into their communication platforms see both risk reduction and performance improvements as representatives gain confidence in real-time guidance.'
      }
    ],
    content: lazy(() => import('./case-studies/communication-platform.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.CommunicationPlatform {...PROJECT_DATA[ProjectRoutes.COMMUNICATION_PLATFORM]} cta={cta} /> }))),
    description: `Video communication system for insurance brokers with AI transcription and real-time summaries, increasing sales 10% and halving training time.`,
    // pdfUri: '/projects/communication-platform.pdf',
  },
  // [ProjectRoutes.PAY_AT_TABLE]: {
  //   title: 'Streamlining Restaurant Payment Operations',
  //   subtitle: 'Reducing table turnover time and increasing payment efficiency through mobile-first solutions',
  //   tags: ['Payment Operations', 'Table Management', 'Operational Efficiency'],
  //   showcase: <Image src='/projects/pay_at_table_1.png' alt='Streamlining Restaurant Payment Operations' />,
  //   banner: <Image src='/projects/restaurant.jpg' alt='Restaurant payment workflow optimization' />,
  // eslint-disable-next-line no-constant-binary-expression
  //   kpis: [] ?? [
  //     { unit: 'x', value: 3, label: 'increase in 5-star Google reviews' },
  //     { unit: '%', value: 10, label: 'increase in staff tips' },
  //     { unit: '%', value: 20, label: 'more payments by card' },
  //     { unit: '%', value: 5, label: 'faster table turnover' },
  //   ],
  //   content: lazy(() => import('./case-studies/pay-at-table.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.PayAtTableSolution {...PROJECT_DATA[ProjectRoutes.PAY_AT_TABLE]} cta={cta} /> }))),
  //   description: `Mobile payment solution using QR/NFC technology to eliminate payment delays, increase digital transaction rates, and improve operational efficiency across restaurant locations.`,
  //   // pdfUri: '/projects/pay-at-table.pdf',
  // },
  [ProjectRoutes.CUSTOMER_PORTAL]: {
    industry: IndustryRoutes['FINANCE'],
    title: 'A Customer Portal for Market Intelligence',
    menuLabel: { company: 'Market Intelligence Firm', label: 'Customer Portal' },
    subtitle: 'Turning stock market index data into actionable insights, delivered through a secure customer portal',
    tags: ['Data Platform', 'Finance'],
    showcase: <Image src='/projects/market-intelligence.jpg' alt='Customer portal for market data' />,
    banner: <Image src='/projects/market-intelligence.jpg' alt='Market intelligence dashboard' />,
    kpis: [],
    summary: [],
    keyTakeaways: [],
    content: undefined,
    description: 'Customer portal aggregating stock market index data, processing it through proprietary algorithms, with customer access and issue flagging.',
  },
  [ProjectRoutes.MEDTECH_NOTIFICATION_SYSTEM]: {
    industry: IndustryRoutes['HEALTHCARE'],
    title: 'Medtech Notification System At Scale',
    subtitle: 'Improve care coordination and response times for medical staff, on-call specialists, and EMTs.',
    menuLabel: { company: 'Hublo', label: 'Communication Platform' },
    tags: ['Workflow Automation', 'Healthcare', 'Operational Efficiency'],
    showcase: <Image src='/projects/surgeons.jpg' alt='Medtech Notification System At Scale' />,
    banner: <Image src='/projects/hospital.png' alt='A hostpital with patients and staff interacting' />,
    info: {
      location: 'France',
      employees: 200,
      revenues: '€30M',
    },
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { value: '90k', label: 'notifications per minute' },
      { unit: 'x', value: 30, label: 'faster notification processing' },
      { unit: 'x', value: 7, label: 'shorter latency at peak usage' },
      { unit: '%', value: 100, label: 'uptime during system migration' },
    ]*/,
    summary: [],
    keyTakeaways: [
      {
        title: 'Healthcare Notification Systems Require Burst Capacity Planning',
        content: 'Healthcare staffing exhibits extreme demand spikes during emergencies and shift changes. Notification systems must be architected for 10x average load rather than typical 2-3x multipliers used in other industries to prevent cascade failures during critical periods.'
      },
      {
        title: 'Zero-Downtime Migrations Need Parallel Processing Validation',
        content: 'Mission-critical systems require sophisticated migration strategies beyond simple blue-green deployments. Real-time message comparison and gradual traffic shifting enable validation of complex business logic without risking service interruption.'
      },
      {
        title: 'Queue Design Impacts Business Outcomes More Than Raw Performance',
        content: 'Intelligent message prioritization and queue partitioning strategies often deliver greater business value than pure throughput improvements. Emergency notifications processed ahead of routine updates can reduce response times by 60% even without infrastructure changes.'
      }
    ],
    content: lazy(() => import('./case-studies/medtech-notification-system.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.MedtechNotificationSystem {...PROJECT_DATA[ProjectRoutes.MEDTECH_NOTIFICATION_SYSTEM]} cta={cta} /> }))),
    description: `Serving critical communications to hundred of thousands of healthcare professionals with a multi-channel notifications system at scale`,
    // pdfUri: '/projects/medtech-notification-system.pdf',
  },
  [ProjectRoutes.RESEARCH_AUTOMATION_PLATFORM]: {
    industry: IndustryRoutes['EDUCATION'],
    title: 'Redefining Scientific Research with AI',
    menuLabel: { company: 'Deep Tech Lab', label: 'Research Automation Platform' },
    subtitle: 'Automating research teams operations and exploration with generative AI',
    tags: ['AI Platform', 'Document Analysis', 'Research & Development'],
    showcase: <Image src='/projects/ai-research.jpg' alt='Redefining Scientific Research with AI' />,
    banner: <Image src='/projects/research.png' alt='Futuristic globe above research data in clouds' style={{ objectPosition: 'center -80px' }} />,
    info: {
      location: 'Austria',
      employees: 15,
      revenues: '€1M',
    },
    // eslint-disable-next-line no-constant-binary-expression
    kpis: []/* ?? [
      { unit: '%', value: 76, label: 'reduction in literature review time' },
      { unit: '%', value: 65, label: 'faster drafting and editing' },
      { unit: '+ TB', value: 3, label: 'scientific literature managed daily' },
      { unit: '%', value: 99.95, label: 'platform uptime' },
    ]*/,
    summary: [],
    keyTakeaways: [
      {
        title: 'Real-Time Academic Data Requires Specialized Infrastructure Architecture',
        content: 'Managing daily updates of scientific literature at terabyte scale demands custom indexing strategies and distributed processing capabilities. Traditional database approaches fail when handling the volume and complexity of academic content with strict uptime requirements for research continuity.'
      },
      {
        title: 'Multi-Format Content Ingestion Complexity Grows Exponentially',
        content: 'Supporting diverse academic content types—from PDFs to multimedia—requires sophisticated preprocessing pipelines and format-specific extraction algorithms. Each new content type introduces unique challenges in maintaining semantic coherence across the platform\'s knowledge base.'
      },
      {
        title: 'Academic Workflow Integration Demands Deep Domain Understanding',
        content: 'Successful adoption in research environments requires understanding subtle academic practices like citation standards, interdisciplinary collaboration patterns, and publication processes. Technical solutions must align with established research methodologies rather than disrupting proven scholarly workflows.'
      }
    ],
    content: undefined,
    // content: lazy(() => import('./case-studies/research-automation-platform.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.ResearchAutomationPlatform {...PROJECT_DATA[ProjectRoutes.RESEARCH_AUTOMATION_PLATFORM]} cta={cta} /> }))),
    description: 'AI platform automating literature reviews with semantic search across millions of papers to accelerate scientific discovery and reduce research time.',
    // pdfUri: '/projects/research-automation-platform.pdf',
  },
  // [ProjectRoutes.BOOK_PUBLISHING_PLATFORM]: {
  //   industry: IndustryRoutes['EDUCATION'],
  //   title: 'B2B Book Creation with AI',
  //   menuLabel: { company: 'StoryOne', label: 'AI Book Creation Platform' },
  //   subtitle: 'Turning ideas, interviews and research into fact-based, print-ready books in under an hour',
  //   tags: ['AI Platform', 'Book Creation', 'Media & Entertainment'],
  //   showcase: <Image src='/projects/story_editor.jpg' alt='AI book creation platform' />,
  //   banner: <Image src='/projects/publishing-store.jpg' alt='A commercial printing machine' />,
  //   kpis: [],
  //   summary: [],
  //   keyTakeaways: [],
  //   content: undefined,
  //   description: 'B2B AI publishing platform turning ideas, interviews and research into fact-based, print-ready non-fiction books in about an hour.',
  // },
  // [ProjectRoutes.HOME_AUTOMATION]: {
  //   industry: IndustryRoutes['ROBOTICS'],
  //   title: 'Empowering Seamless Connectivity with a Custom IoT Platform',
  //   subtitle: 'Intuitive and faster interactions, connecting families and their homes together',
  //   menuLabel: { company: 'Consumer-Electronics Manufacturer', label: 'Communication Platform' },
  //   tags: ['Smart Home Connectivity', 'IoT Ecosystem', 'Seamless Device Integration'],
  //   showcase: <Image src='/projects/smart_home.jpg' alt='Empowering Seamless Connectivity with a Custom IoT Platform' />,
  //   banner: <Image src='/projects/home.jpg' alt='A smart home with a voice assistant' />,
  //   kpis: [
  //     { unit: 'x', value: 10, label: 'faster interaction time' },
  //     { unit: '%', value: 25, label: 'reduced cloud infra costs' },
  //     { unit: '%', value: 60, label: 'less customer support tickets' },
  //     { unit: '%', value: 99.99, label: 'platform and systems uptime' },
  //   ],
  //   content: lazy(() => import('./case-studies/home-automation').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.HomeAutomation {...PROJECT_DATA[ProjectRoutes.HOME_AUTOMATION]} cta={cta} /> }))),
  //   // pdfUri: '/projects/home-automation.pdf',
  // },
  // [ProjectRoutes.BOHO_BABY_MARKET]: {
  //   title: 'Sustainable Baby Fashion Marketplace',
  //   subtitle: 'Empowering parents to trade eco-friendly garments for their children',
  //   tags: ['Sustainable Fashion', 'Eco-Friendly Marketplace', 'Parenting Solutions'],
  //   showcase: <Image src='/projects/boho_baby_market_1.png' alt='Sustainable baby fashion marketplace' />,
  //   banner: <Image src='/projects/baby.jpg' alt='Two parents and their baby in autumn golden leaves' />,
  //   kpis: [
  //     // { value: '3 months', label: 'MVP development duration' },
  //     { unit: '%', value: 59, label: 'checkout completion rate' },
  //     { unit: '%', value: 82, label: 'onboarding completion rate' },
  //     { unit: '%', value: 99.99, label: 'platform and systems uptime' },
  //   ],
  //   content: lazy(() => import('./case-studies/boho-baby-market.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.BohoBabyMarket {...PROJECT_DATA[ProjectRoutes.BOHO_BABY_MARKET]} cta={cta} /> }))),
  //   // pdfUri: '/projects/boho-baby-market.pdf',
  // },
  // [ProjectRoutes.PART_TIMERZ]: {
  //   title: 'Home Services Marketplace Platform',
  //   subtitle: 'Connecting homeowners with local service providers',
  //   tags: ['Home Services Marketplace', 'Local Service Connection', 'User Engagement Platform'],
  //   showcase: <Image src='/projects/part_timerz_1.png' alt='Home Services Marketplace Platform' />,
  //   banner: <Image src='/projects/cleaning.jpg' alt='A person cleaning a house' />,
  //   kpis: [
  //     { unit: '%', value: 65, label: 'user retention rate' },
  //     { unit: '%', value: 80, label: 'bookings within first 3 months' },
  //     { unit: '%', value: 99.9, label: 'platform and systems uptime' },
  //   ],
  //   content: lazy(() => import('./case-studies/part-timerz.tsx').then(module => ({ default: ({ cta }: CaseStudyProps) => <module.PartTimerz {...PROJECT_DATA[ProjectRoutes.PART_TIMERZ]} cta={cta} /> }))),
  //   // pdfUri: '/projects/part-timerz.pdf',
  // },
} satisfies { [key in ProjectRoutes]?: ProjectData };

export const getPastProjectIds = (projectId: ProjectRoutes, count: number): ProjectRoutes[] => {
  const projectRoutes = Object.keys(PROJECT_DATA).filter(p => PROJECT_DATA[p as ProjectRoutes].menuLabel) as ProjectRoutes[];
  const projectIndex = projectRoutes.indexOf(projectId);
  const pastProjects = projectRoutes.slice(projectIndex - count, projectIndex);
  if (pastProjects.length < count) {
    pastProjects.unshift(...projectRoutes.slice(projectRoutes.length - (count - pastProjects.length)));
  }

  return pastProjects;
}

export const getNextProjectIds = (projectId: ProjectRoutes, count: number): ProjectRoutes[] => {
  const projectRoutes = Object.keys(PROJECT_DATA).filter(p => PROJECT_DATA[p as ProjectRoutes].menuLabel) as ProjectRoutes[];
  const projectIndex = projectRoutes.indexOf(projectId);
  const nextProjects = projectRoutes.slice(projectIndex + 1, projectIndex + 1 + count);
  if (nextProjects.length < count) {
    nextProjects.push(...projectRoutes.slice(0, count - nextProjects.length));
  }

  return nextProjects;
}
